import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../utils/api';
import api from '../utils/api';
import { MATRIX } from '../utils/roles';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [exitStatus, setExitStatus] = useState(null);

  const fetchMe = useCallback(async () => {
    // If we just logged out, don't try to fetch again until page reload
    if (sessionStorage.getItem('mi_hrm_logout_pending')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await authApi.me();
      if (data.success) {
        setUser(data.data);
        setLoading(false);
        // Fetch exit request status asynchronously without blocking initial page render
        Promise.all([
          api.get('/exit-requests', { params: { status: 'pending', pageSize: 1 } }).catch(() => null),
          api.get('/exit-requests', { params: { status: 'approved', pageSize: 1 } }).catch(() => null)
        ]).then(([exitRes, exitRes2]) => {
          if (exitRes?.data?.data?.length > 0) setExitStatus(exitRes.data.data[0].status);
          else if (exitRes2?.data?.data?.length > 0) setExitStatus(exitRes2.data.data[0].status);
          else setExitStatus(null);
        }).catch(() => setExitStatus(null));
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    fetchMe(); 
  }, [fetchMe]);

  const login = async (credentials) => {
    sessionStorage.removeItem('mi_hrm_logout_pending');
    const { data } = await authApi.login(credentials);
    if (data.success) {
      if (data.access_token) sessionStorage.setItem('mi_hrm_token', data.access_token);
      setUser(data.data);
    }
    return data;
  };

  const logout = useCallback(async () => {
    // 1. Mark as logging out to prevent fetchMe loops
    sessionStorage.setItem('mi_hrm_logout_pending', '1');
    sessionStorage.removeItem('mi_hrm_token');
    
    // 2. Clear local state immediately
    setUser(null);
    
    // 3. Inform backend and wait briefly
    try { 
      await authApi.logout(); 
    } catch {}
    
    // 4. Force a clean redirect
    window.location.href = '/login';
  }, []);

  // Inactivity Timeout (5 minutes)
  useEffect(() => {
    if (!user) return;
    
    let timeout;
    let lastReset = 0;
    
    const resetTimer = () => {
      const now = Date.now();
      // Throttle: only reset timer at most once every 2 seconds to avoid layout/reflow overhead from mousemove/scroll
      if (now - lastReset > 2000) {
        lastReset = now;
        clearTimeout(timeout);
        timeout = setTimeout(() => { 
          logout(); 
        }, 5 * 60 * 1000); // 5 minutes
      }
    };

    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    
    // Set initial timer immediately
    clearTimeout(timeout);
    timeout = setTimeout(() => { 
      logout(); 
    }, 5 * 60 * 1000);

    return () => {
      clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  const can = (module, action) => {
    if (!user) return false;
    if (['system_admin', 'it_manager'].includes(user.role)) return true; // Super admin override

    // 1. If checking an explicit ess_* module or if an explicit DB row exists for an ess_* module, honor it first
    if (Array.isArray(user.permissions) && user.permissions.length > 0) {
      const essMaster = user.permissions.find(item => item.module === 'ess_portal_access');
      if (essMaster && essMaster.can_view === false) {
        return false;
      }
      if (String(module).startsWith('ess_')) {
        const explicitEss = user.permissions.find(item => item.module === module);
        if (explicitEss !== undefined) {
          return Boolean(explicitEss[`can_${action}`]);
        }
      }
    }

    // 2. Default ESS Self-Service Modules bypass when not overridden by an explicit ess_* key
    const isEssModule = [
      'employee_self_svc',
      'leave_management',
      'attendance_tracker',
      'payslip_generator',
      'performance_reviews',
      'training_certs',
      'grievance_mgmt',
      'employee_records' // needed to view/edit own profile
    ].includes(module);

    if (isEssModule && (action === 'view' || action === 'create' || action === 'edit')) {
      return true;
    }

    if (module === 'org_chart' && action === 'view') {
      return true;
    }

    if (user.permissions && user.permissions.length > 0) {
      const p = user.permissions.find(item => item.module === module);
      if (p) return p[`can_${action}`] === true;
    }

    const rolePerm = MATRIX[user.role]?.[module];
    if (rolePerm) {
      return rolePerm[action] === true;
    }
    return false;
  };

  const isExitLocked = exitStatus === 'pending' || exitStatus === 'approved';

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, fetchMe, can, exitStatus, isExitLocked, setExitStatus }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
